import React, { useState } from 'react';
import LineChart from '../../../common/components/charts/LineChart';
import PropTypes from 'prop-types';
import ErrorScreenMessage from '../../ErrorScreenMessage/ErrorScreenMessage';
import { convertDateFormat } from '../../../helper/convertDate';
import {
	NETSALES,
	ONHAND_INVENTORY,
	INVENTORY_TRENDS_SUB_HEADER,
} from '../../../constants/constant';
import DownloadTrend from '../../Download/DownloadTrend';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Box, CircularProgress } from '@material-ui/core';
import { colors } from '../../../theme/color-palette';
import DefinitionDrawer, {
	DefinitionContext,
} from '../../../components/Definition/index';
import { getChartType } from '../../../components/Definition';

const { PURPLE, PURPLE_INACTIVE, LIGHT_GREY, FONT_COLOR } = colors;

// Styles
const useStyles = makeStyles(() => ({
	container: {
		width: (P) => P.chartWidth,
		border: `1px solid ${LIGHT_GREY}`,
		borderRadius: '8px',
		height: '430px !important',
	},
	loaderContainer: {
		height: '422px',
		display: 'flex',
	},
	chartTitle: {
		textAlign: 'left',
		fontSize: '18px',
		color: FONT_COLOR,
		fontFamily: 'Bogle-Regular',
		cursor: 'pointer',
	},
	circularProgress: {
		margin: 'auto',
	},
	chartHeader: {
		width: (P) => P.chartWidth,
		height: '63px',
		padding: '16px',
	},
	chartSubTitle: {
		textAlign: 'left',
		fontSize: '16px',
		color: FONT_COLOR,
		fontFamily: 'Bogle-Light',
		fontWeight: 300,
		marginTop: '4px',
	},
	invTrendHeader: {
		display: 'flex',
	},
}));

const InventoryTrends = (props) => {
	const {
		chartName,
		chartWidth,
		chartTitle,
		chartHeight,
		chartData,
		weekFilter,
		errorMsg,
		category,
		isLoading,
		showWeekDates,
		tab,
		selectedTab,
	} = props;
	const classes = useStyles({ chartWidth });

	const isColumn = weekFilter <= 0 && chartData?.data?.length <= 1;

	const xAxisData = {
		date: 'busDt',
		nonDate: weekFilter === 0 ? 'busDt' : 'wmYrWkNbr',
	};
	const initialCxt = {
		isDrawerOpened: false,
		metricType: '',
	};
	const [definition, setDefinition] = useState(initialCxt);

	const cxtValue = React.useMemo(
		() => ({ definition, setDefinition }),
		[definition]
	);

	const openDefinition = () => {
		setDefinition({
			isDrawerOpened: true,
			metricType: getChartType(chartTitle),
		});
	};
	const chartValue = [
		{
			value: 'lyValue',
			name: ONHAND_INVENTORY,
			chartLine: '0',
			color: PURPLE,
			chartType: isColumn ? 'Column' : 'Line',
		},
		{
			value: 'tyValue',
			name: NETSALES,
			chartLine: '0',
			color: PURPLE_INACTIVE,
			chartType: isColumn ? 'Column' : 'Line',
		},
	];

	const parseData = (data = []) => {
		return data?.map((item) => {
			return {
				...item,
				tyValue: item.tyValue,
				lyValue: item.lyValue,
				excel_ty_value: item.tyValue,
				excel_ly_value: item.lyValue,
				busDt: convertDateFormat(item.busDt),
				week_number: item.wmYrWkNbr,
				wmWkNbr: item.wmWkNbr,
			};
		});
	};

	const trendTemplate = !chartData?.data ? (
		<ErrorScreenMessage errorMsg={errorMsg} chartTitle={chartTitle} />
	) : (
		<>
			<Box className={classes.invTrendHeader}>
				<Box className={classes.chartHeader}>
					<Typography className={classes.chartTitle} onClick={openDefinition}>
						{chartTitle}
					</Typography>
					<Typography className={classes.chartSubTitle}>
						{INVENTORY_TRENDS_SUB_HEADER}
					</Typography>
				</Box>
				<Box>
					<DownloadTrend
						data={parseData(chartData?.data)}
						chartTitle={chartTitle}
						weekFilter={weekFilter}
						category={category}
						inventory={true}
						tab={tab}
						selectedTab={selectedTab}
					/>
				</Box>
			</Box>
			<LineChart
				chartName={chartName}
				chartData={parseData(chartData?.data) || []}
				chartValue={chartValue}
				chartWidth={chartWidth}
				chartHeight={chartHeight}
				weekFilter={weekFilter}
				xAxisData={xAxisData}
				isCompositChart={true}
				showExploreButton={false}
				isAmount={true}
				showWeekDates={showWeekDates}
			/>
		</>
	);

	return (
		<Box className={classes.container}>
			{isLoading ? (
				<Box className={classes.loaderContainer}>
					<CircularProgress className={classes.circularProgress} />
				</Box>
			) : (
				trendTemplate
			)}
			<DefinitionContext.Provider value={cxtValue}>
				<DefinitionDrawer
					isOpen={definition.isDrawerOpened}
					metricType={definition.metricType}
					onClose={() => setDefinition(initialCxt)}
				/>
			</DefinitionContext.Provider>
		</Box>
	);
};

InventoryTrends.defaultProps = {
	chartData: {},
	chartName: 'dashboardChart',
	chartWidth: '100%',
	chartHeight: '326px',
	chartTitle: 'Inventory Trends Chart',
	weekFilter: '',
};

InventoryTrends.propTypes = {
	chartData: PropTypes.shape({
		data: PropTypes.arrayOf(PropTypes.shape({})),
	}),
	chartHeight: PropTypes.string,
	chartWidth: PropTypes.string,
	chartName: PropTypes.string,
	chartTitle: PropTypes.string,
	weekFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	errorMsg: PropTypes.string,
	category: PropTypes.string,
	isLoading: PropTypes.bool,
	showWeekDates: PropTypes.bool,
	tab: PropTypes.string,
	selectedTab: PropTypes.string,
};

export default InventoryTrends;
